<script>
import axios from '@/utils/axios';

export default {
  inject: ['setLoading'],
  data() {
    return {
      loading: true,
      documents: [],
      previewHtml: '',
      previewLoading: false,
      previewDoc: null,
    };
  },
  mounted() {
    this.fetchDocuments();
  },
  methods: {
    async fetchDocuments() {
      this.loading = true;
      try {
        const response = await axios.get('/hrms/hrms-documents/my');
        this.documents = response.data.data || [];
      } catch (error) {
        this.$swalToast(error.message, 'error');
      } finally {
        this.loading = false;
      }
    },

    typeLabel(t) {
      const map = {
        offer_letter: 'Offer Letter',
        appointment_letter: 'Appointment Letter',
        experience_letter: 'Experience Letter',
        relieving_letter: 'Relieving Letter',
        general: 'General',
      };
      return map[t] || t || '-';
    },

    typeClass(type) {
      const map = {
        offer_letter: 'md-tag md-offer',
        appointment_letter: 'md-tag md-appointment',
        experience_letter: 'md-tag md-experience',
        relieving_letter: 'md-tag md-relieving',
        general: 'md-tag md-general',
      };
      return map[type] || 'md-tag md-general';
    },

    // The preview endpoint always requires employee_id as a query param -
    // for the employee's own view, we pull their employee_id from the
    // cached auth_user.employee record (set on login / periodic refresh)
    currentEmployeeId() {
      try {
        const auth_user = JSON.parse(localStorage.getItem('auth_user') || '{}');
        return auth_user?.employee?.id || null;
      } catch (e) {
        return null;
      }
    },

    async openPreview(doc) {
      this.previewDoc = doc;
      this.previewHtml = '';
      this.previewLoading = true;
      this.$openModal('hrm_my_document_preview_modal');

      const employeeId = this.currentEmployeeId();
      if (!employeeId) {
        this.$swalToast('Could not determine your employee profile.', 'error');
        this.previewLoading = false;
        return;
      }

      try {
        const documentId = doc.hrms_document_id || doc.document?.id || doc.id;
        const res = await axios.get(`/hrms/hrms-documents/${documentId}/preview`, {
          params: { employee_id: employeeId },
        });
        this.previewHtml = res.data.data?.html || '';
      } catch (error) {
        this.$swalToast(error.message, 'error');
      } finally {
        this.previewLoading = false;
      }
    },
  },
};
</script>

<template>
  <layout-header></layout-header>
  <layout-sidebar></layout-sidebar>

  <div class="page-wrapper">
    <div class="content">

      <!-- Header -->
      <div class="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
        <h4 class="page-title mb-0">My Documents</h4>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="md-state">
        <div class="spinner-border text-primary mb-2"></div>
        <div>Loading documents...</div>
      </div>

      <!-- Empty -->
      <div v-else-if="!documents.length" class="md-state">
        <i class="ti ti-file-off d-block mb-2" style="font-size:38px; opacity:.35;"></i>
        No documents assigned to you yet.
      </div>

      <!-- Card Grid -->
      <div v-else class="row g-3">
        <div v-for="doc in documents" :key="doc.id" class="col-xxl-4 col-lg-6 col-md-6">
          <div class="md-card" @click="openPreview(doc)">
            <div class="md-icon"><i class="ti ti-file-text"></i></div>
            <div class="md-info">
              <span :class="typeClass(doc.document?.type || doc.type)">
                {{ typeLabel(doc.document?.type || doc.type) }}
              </span>
              <h6 class="md-name">{{ doc.document?.name || doc.name }}</h6>
              <span class="md-date">
                <i class="ti ti-calendar-event me-1"></i>
                Assigned {{ $formatDate(doc.assigned_at || doc.created_at) }}
              </span>
            </div>
            <i class="ti ti-chevron-right md-arrow"></i>
          </div>
        </div>
      </div>

    </div>
    <app-footer></app-footer>
  </div>

  <!-- Preview Modal -->
  <div class="modal fade" id="hrm_my_document_preview_modal">
    <div class="modal-dialog modal-dialog-centered modal-lg">
      <div class="modal-content">
        <div class="modal-header">
          <h4 class="modal-title">{{ previewDoc?.document?.name || previewDoc?.name || 'Document' }}</h4>
          <button type="button" class="btn-close custom-btn-close" data-bs-dismiss="modal" aria-label="Close">
            <i class="ti ti-x"></i>
          </button>
        </div>
        <div class="modal-body">
          <div v-if="previewLoading" class="text-center py-4 text-muted">
            <div class="spinner-border spinner-border-sm text-primary mb-2"></div>
            <div>Loading document...</div>
          </div>
          <div v-else class="md-preview-box" v-html="previewHtml"></div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-light" data-bs-dismiss="modal">Close</button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.md-state {
  text-align: center; padding: 60px 20px;
  color: #94a3b8; font-size: 13.5px;
  background: #fff; border-radius: 14px;
}

.md-card {
  display: flex; align-items: center; gap: 14px;
  background: #fff; border: 1px solid #eef2f7; border-radius: 14px;
  padding: 16px; cursor: pointer; height: 100%;
  transition: all .18s ease;
}
.md-card:hover { border-color: #c7d2fe; box-shadow: 0 8px 20px rgba(99,102,241,.1); }

.md-icon {
  width: 46px; height: 46px; border-radius: 12px;
  background: #eef2ff; color: #4f46e5;
  display: flex; align-items: center; justify-content: center;
  font-size: 20px; flex-shrink: 0;
}

.md-info { flex: 1; min-width: 0; }
.md-name {
  font-size: 14px; font-weight: 700; color: #1e293b; margin: 4px 0 2px;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.md-date { font-size: 11px; color: #94a3b8; }
.md-arrow { color: #cbd5e1; font-size: 18px; flex-shrink: 0; }

.md-tag {
  display: inline-block; font-size: 10px; font-weight: 600;
  padding: 2px 9px; border-radius: 20px;
}
.md-offer { background: #eef2ff; color: #4f46e5; }
.md-appointment { background: #e0f2fe; color: #0369a1; }
.md-experience { background: #fef3c7; color: #b45309; }
.md-relieving { background: #fee2e2; color: #dc2626; }
.md-general { background: #f1f5f9; color: #475569; }

.md-preview-box {
  border: 1px solid #eef2f7; border-radius: 10px;
  padding: 18px; max-height: 400px; overflow-y: auto;
  font-size: 13.5px; color: #334155; line-height: 1.8;
}
</style>